import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../../context/LangContext";
import { t } from "../../lib/i18n";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, MapPin, InstagramLogo, FacebookLogo } from "@phosphor-icons/react";

export default function Footer() {
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      setBusy(true);
      await api.post("/newsletter", { email });
      toast.success(lang === "gr" ? "Εγγραφήκατε!" : "Subscribed!");
      setEmail("");
    } catch {
      toast.error(lang === "gr" ? "Αποτυχία" : "Failed");
    } finally { setBusy(false); }
  };

  return (
    <footer className="relative bg-[#0a0a0c] border-t border-white/5 mt-20" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <img src="/mpsound-logo-neon.png" alt="MP Sound" className="h-12 w-auto mb-4" />
          <p className="text-gray-400 text-sm max-w-sm">{t("footer.tagline", lang)}</p>
          <div className="flex gap-4 mt-6">
            <a href="https://www.instagram.com/aa_mpsound?igsh=MTJ3am5hbDEyNmdhbQ==" target="_blank" rel="noreferrer" className="text-gray-400 hover:text-[#ccff00]" data-testid="footer-instagram">
              <InstagramLogo size={22} weight="bold" />
            </a>
            <a href="https://www.facebook.com/aampsound" target="_blank" rel="noreferrer" className="text-gray-400 hover:text-[#ccff00]" data-testid="footer-facebook">
              <FacebookLogo size={22} weight="bold" />
            </a>
          </div>

          {/* Authorized Dealer badge — Amate Audio */}
          <Link
            to="/brands/amate-audio"
            className="mt-8 inline-flex items-center gap-4 border border-white/10 hover:border-[#ccff00]/40 bg-[#0c0c10] px-4 py-3 transition-colors group"
            data-testid="footer-amate-badge"
          >
            <img src="/brands/amate-audio-logo.png" alt="Amate Audio" className="h-12 w-12 object-contain" />
            <div className="leading-tight">
              <div className="mono-font text-[9px] tracking-[0.25em] uppercase text-[#ccff00]">
                {lang === "gr" ? "Επίσημος Αντιπρόσωπος" : "Authorized Dealer"}
              </div>
              <div className="display-font text-sm font-bold uppercase text-white group-hover:text-[#ccff00] transition-colors">
                Amate Audio
              </div>
            </div>
          </Link>
        </div>

        <div className="md:col-span-3">
          <div className="label-tag mb-4">{lang === "gr" ? "Πλοήγηση" : "Navigation"}</div>
          <ul className="space-y-2 text-sm text-gray-300">
            <li><Link to="/services" className="hover:text-[#ccff00]">{t("nav.services", lang)}</Link></li>
            <li><Link to="/products" className="hover:text-[#ccff00]">{t("nav.products", lang)}</Link></li>
            <li><Link to="/portfolio" className="hover:text-[#ccff00]">{t("nav.portfolio", lang)}</Link></li>
            <li><Link to="/blog" className="hover:text-[#ccff00]">{t("nav.blog", lang)}</Link></li>
            <li><Link to="/vlogs" className="hover:text-[#ccff00]">{t("nav.vlogs", lang)}</Link></li>
            <li><Link to="/booking" className="hover:text-[#ccff00]">{t("nav.booking", lang)}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="label-tag mb-4">{lang === "gr" ? "Επικοινωνία" : "Contact"}</div>
          <ul className="space-y-3 text-sm text-gray-300">
            <li className="flex items-center gap-2"><Phone size={16} className="text-[#ccff00]" /> +357 22574428</li>
            <li className="flex items-center gap-2"><Phone size={16} className="text-[#ccff00]" /> +357 99574428</li>
            <li className="flex items-center gap-2"><EnvelopeSimple size={16} className="text-[#ccff00]" /> info@mpsound.eu</li>
            <li className="flex items-center gap-2"><MapPin size={16} className="text-[#ccff00]" /> Cyprus</li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="label-tag mb-4">{t("footer.newsletter_title", lang)}</div>
          <p className="text-sm text-gray-400 mb-4">{t("footer.newsletter_sub", lang)}</p>
          <form onSubmit={subscribe} className="flex flex-col gap-3" data-testid="newsletter-form">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="input-dark"
              data-testid="newsletter-email"
            />
            <button type="submit" disabled={busy} className="btn-primary justify-center" data-testid="newsletter-submit">
              {busy ? "..." : t("cta.subscribe", lang)}
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/5 py-6 mono-font text-xs text-gray-500 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 px-5 sm:px-8">
        <span>© {new Date().getFullYear()} MP SOUND — A.A. MODEL POWER LTD. {t("footer.rights", lang)}</span>
        <Link to="/admin/login" className="hover:text-[#ccff00] tracking-[0.2em] uppercase" data-testid="footer-admin-link">
          Admin
        </Link>
      </div>
    </footer>
  );
}
